import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProfileSetupGuardProps {
  children: React.ReactNode;
}

const ProfileSetupGuard: React.FC<ProfileSetupGuardProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-200">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, let other routes handle it (redirect to login)
  if (!user) {
    return <>{children}</>;
  }

  // If already on profile page, don't redirect
  if (location.pathname === '/profile') {
    return <>{children}</>;
  }

  // Check if user needs to complete profile setup
  const needsProfileSetup = !user.username || user.firstName === 'User';

  // Force new users to complete profile setup (username is required and first name can't be "User")
  if (needsProfileSetup) {
    return <Navigate to="/profile?setup=true" replace />;
  }

  // User has completed profile setup, render normal content
  return <>{children}</>;
};

export default ProfileSetupGuard;